import { People } from "@material-ui/icons";
import React from "react";
import * as ISchema from "@schema";
import {
  eventIconClasses,
  EventSchedulingQueryEvent,
  EventSchedulingQueryOrg,
  EventSchedulingQueryPerson,
  getOrderedAttendeesAndPersonsMaps,
} from "./EventScheduling";
import FocusTimeToolTip, { getName } from "./FocusTimeTooltip";
import { AvatarPerson } from "@components/avatar/AvatarPerson";
import { AvatarStack } from "@clockwise/web-commons/src/ui/avatar-stack/AvatarStack";

export const getAvatarPerson = (
  person: EventSchedulingQueryPerson,
  fallbackCalendarId?: string,
) => {
  const profile = person?.profile;

  // TS Validation
  // since `<Avatar />` depends on old ISchema types and its typing cannot be easily changes
  // (because tons of components depend on it)
  // and since we want the Avatar to show empty data as well
  // we coerce `person` into `avatarPerson` a AvatarPersonRedux-type-friendly value.
  const EMPTY_PROFILE: ISchema.IPerson["profile"] = {
    __typename: "UserProfile",
    givenName: "",
    familyName: "",
    externalImageUrl: "",
  };

  return {
    ...person,
    profile: {
      profile,
      externalImageUrl: profile?.externalImageUrl || EMPTY_PROFILE.externalImageUrl,
      familyName: profile?.familyName || EMPTY_PROFILE.familyName,
      givenName:
        profile?.givenName || person?.primaryEmail || fallbackCalendarId || EMPTY_PROFILE.givenName,
      __typename: profile?.__typename || EMPTY_PROFILE.__typename,
    },
  };
};

const EventAttendeesAvatars = ({
  org,
  event,
}: {
  org?: EventSchedulingQueryOrg | null;
  event: EventSchedulingQueryEvent | undefined;
}) => {
  if (!event || !org) {
    return null;
  }

  const { personImpacts, personMap, attendeeMap } = getOrderedAttendeesAndPersonsMaps({
    org,
    event: event,
  });

  const overlap = personImpacts.length > 5;

  return (
    <div className="cw-inline-flex cw-items-center">
      <People className={eventIconClasses} />
      <AvatarStack maxShown={9} overlap={overlap} size="medium">
        {personImpacts.map((personImpact) => {
          const person = personMap[personImpact.calendarId] || null;
          const attendee = attendeeMap[personImpact.calendarId] || null;
          const { impacts } = personImpact;
          const avatarPerson = getAvatarPerson(person, impacts?.calendarId);

          return (
            <AvatarPerson
              key={personImpact.calendarId}
              person={avatarPerson}
              orgPerson={null}
              toolTipContent={
                <FocusTimeToolTip name={getName(person, personImpact)} impacts={impacts} />
              }
              backupCalendarId={personImpact.calendarId}
              size="medium"
              responseStatus={attendee?.responseStatus}
            />
          );
        })}
      </AvatarStack>
    </div>
  );
};

export default EventAttendeesAvatars;
